import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { withLoadingToast } from "utils/withLoadingToast";
import { toast } from "react-toastify";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  cancelPayment,
  downloadPaymentReceipt,
} from "features/Payments/services/PaymentService";
import {
  PaymentStatus,
  paymentStatusMap,
} from "features/Payments/constants/payment.constants";
import { usePaymentOverview } from "./PaymentOverviewContext";
import OverviewTab from "./OverviewTab";

function paymentErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

const Section = () => {
  const { payment, refetch } = usePaymentOverview();
  const { can } = useAccess();
  const canReadPayment = can(Permissions.PAYMENT.READ);
  const canCancelPayment = can(Permissions.PAYMENT.CANCEL);
  const [activeTab, setActiveTab] = useState("1");
  const [receiptDownloading, setReceiptDownloading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  if (!payment) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This payment could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  const handleDownloadReceipt = async () => {
    setReceiptDownloading(true);
    try {
      await withLoadingToast(
        "Preparing receipt…",
        () => downloadPaymentReceipt(payment.id, payment.code),
        "Receipt downloaded",
        "Could not download receipt",
        (e) => (e instanceof Error ? e.message : "Could not download receipt"),
      );
    } finally {
      setReceiptDownloading(false);
    }
  };

  const handleConfirmCancelPayment = async () => {
    const toastId = toast.loading("Cancelling payment…");
    try {
      const res = await cancelPayment(payment.id);
      if (!res.success) {
        throw new Error(res.message || "Could not cancel payment");
      }
      toast.update(toastId, {
        render: "Payment cancelled",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      setCancelModalOpen(false);
      await refetch();
    } catch (err) {
      toast.update(toastId, {
        render: paymentErrorMessage(err, "Could not cancel payment"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  return (
    <React.Fragment>
      <ConfirmActionModal
        show={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title={payment.code}
        iconClass="ri-close-circle-line"
        iconColor="text-danger"
        heading="Cancel this payment?"
        description={`This will void payment ${payment.code} and update linked records (e.g. invoice balance). This action may not be reversible.`}
        confirmText="Cancel payment"
        confirmColor="danger"
        cancelText="Keep payment"
        onConfirm={() => void handleConfirmCancelPayment()}
      />
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{payment.code}</h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={payment.status}
                            map={paymentStatusMap}
                          />
                        </div>
                        {payment.created_at ? (
                          <>
                            <div className="vr" />
                            <div>
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={payment.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                        {payment.deleted_at ? (
                          <>
                            <div className="vr" />
                            <span
                              className="badge bg-danger"
                              title={payment.deleted_at}
                            >
                              Deleted
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto">
                    <div className="hstack gap-2 flex-nowrap">
                      {canReadPayment && payment.status === PaymentStatus.POSTED ? (
                        <Button
                          type="button"
                          color="primary"
                          size="sm"
                          className="btn-label"
                          disabled={receiptDownloading}
                          onClick={() => void handleDownloadReceipt()}
                        >
                          <i className="ri-file-pdf-2-line label-icon align-middle fs-14" />
                          {receiptDownloading ? "Downloading…" : "Download receipt"}
                        </Button>
                      ) : null}
                      {canCancelPayment && payment.status === PaymentStatus.POSTED ? (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            type="button"
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-more-2-fill align-middle" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem
                              className="text-danger"
                              onClick={() => setCancelModalOpen(true)}
                            >
                              <i className="ri-close-circle-line align-bottom me-2" />
                              Cancel payment
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
